import dayjs from '../../utils/dayjs';
import { Badge, BlockStack, Card, Divider, EmptyState, InlineGrid, InlineStack, Layout, Page, Text, Link } from '@shopify/polaris';
import SkeletonLoading from 'components/skeletonPageLoading';
import { ReactECharts, ReactEChartsProps } from 'components/React-ECharts';

import React, { useCallback, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { ChargeTransaction, useGetTransactionDetail } from 'queries/charge_station.query';
import emptyIMG from 'media/images/empty.png';

export default function ChargeTransactionView() {
  let { charge_transaction_id } = useParams();
  const { mutateAsync: transactionDetail, data: transactionDetailData, isPending, isSuccess } = useGetTransactionDetail();
  const history = useNavigate();

  const fetchData = useCallback(async (charge_transaction_id) => {
    await transactionDetail(charge_transaction_id);
  }, []);

  useEffect(() => {
    if (charge_transaction_id) {
      fetchData(charge_transaction_id);
    }
  }, [charge_transaction_id, fetchData, transactionDetail]);

  const transaction = transactionDetailData?.transaction;
  const series = transactionDetailData?.series;

  const formatDuration = (seconds?: number | null) => {
    if (typeof seconds !== 'number') {
      return '-';
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return [hours, minutes, secs].map((value) => String(value).padStart(2, '0')).join(':');
  };

  const formatNumber = (value?: number | null, suffix?: string) => {
    if (typeof value !== 'number') {
      return '-';
    }

    const formatter = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 2 });
    return suffix ? `${formatter.format(value)} ${suffix}` : formatter.format(value);
  };

  const renderStatusBadge = (status?: ChargeTransaction['status']) => {
    switch (status) {
      case 'STARTED':
        return <Badge tone="warning">Đang sạc</Badge>;
      case 'STOPPED':
        return <Badge tone="success">Hoàn tất</Badge>;
      default:
        return (
          <Text as="span" tone="subdued">
            -
          </Text>
        );
    }
  };

  const vehicleLabel = transaction?.vehicle?.licensePlate ?? transaction?.vehicle?.tagId ?? '-';
  const stationLabel = transaction?.charge_station?.cpid + '/' + (transaction?.charge_station?.name ?? '-');
  const connectorLabel = typeof transaction?.connectorId === 'number' ? `Trụ ${transaction.connectorId}` : '-';
  const socStartLabel = typeof transaction?.metrics?.socStart === 'number' ? `${transaction?.metrics?.socStart}%` : '-';
  const socEndLabel = typeof transaction?.metrics?.socEnd === 'number' ? `${transaction?.metrics?.socEnd}%` : '-';
  const socSummary = `${socStartLabel} → ${socEndLabel}`;
  const energyValue = transaction?.metrics?.energyKwh ?? transaction?.metrics?.sessionKw;
  const totalEnergyLabel = formatNumber(energyValue ?? null, 'kWh');

  const chartOptions = useMemo(() => {
    const toSeriesData = (points?: { timestamp: string; value: number }[]) => points?.map((point) => [point.timestamp, point.value]) ?? [];

    const buildOption = (label: string, unit: string, dataPoints?: { timestamp: string; value: number }[]): ReactEChartsProps['option'] => ({
      tooltip: {
        trigger: 'axis',
      },
      grid: {
        left: 40,
        right: 40,
        top: 40,
        bottom: 40,
      },
      xAxis: {
        type: 'time',
        boundaryGap: [0, 0],
      },
      yAxis: {
        type: 'value',
        name: unit,
        position: 'left',
      },
      series: [
        {
          name: label,
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: toSeriesData(dataPoints),
        },
      ],
    });

    return {
      power: buildOption('Công suất (kW)', 'kW', series?.powerKw),
      current: buildOption('Dòng điện (A)', 'A', series?.currentA),
      voltage: buildOption('Điện áp (V)', 'V', series?.voltageV),
    };
  }, [series]);

  const hasPowerData = (series?.powerKw?.length ?? 0) > 0;
  const hasCurrentData = (series?.currentA?.length ?? 0) > 0;
  const hasVoltageData = (series?.voltageV?.length ?? 0) > 0;

  if (isPending && !transactionDetailData) {
    return <SkeletonLoading />;
  }

  return (
    <>
      <Helmet>
        <title>Chi tiết phiên sạc</title>
      </Helmet>
      <Page
        title={`Phiên sạc ${transaction?.ocppTransactionId ? `#${transaction?.ocppTransactionId}` : ''}`}
        backAction={{
          content: 'Quay lại danh sách',
          onAction: () => {
            history('/charge_transaction');
          },
        }}
      >
        {transaction ? (
          <Layout>
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <Text as="h2" variant="headingMd">
                        Tổng quan phiên sạc
                      </Text>
                      <Text as="span" tone="subdued">
                        Bắt đầu lúc {transaction.startedAt ? dayjs(new Date(transaction.startedAt)).format('HH:mm DD-MM-YYYY') : '-'}
                      </Text>
                    </BlockStack>
                    {renderStatusBadge(transaction.status)}
                  </InlineStack>

                  <Divider />

                  <InlineGrid columns={{ xs: '1', sm: '1', md: '3', lg: '3' }} gap="400">
                    <Card padding="300">
                      <BlockStack gap="100">
                        <Text as="span" tone="subdued">
                          Tổng điện năng tiêu thụ
                        </Text>
                        <Text as="span" variant="heading2xl">
                          {totalEnergyLabel}
                        </Text>
                      </BlockStack>
                    </Card>
                    <Card padding="300">
                      <BlockStack gap="100">
                        <Text as="span" tone="subdued">
                          Thời gian sạc
                        </Text>
                        <Text as="span" variant="heading2xl">
                          {formatDuration(transaction.metrics?.durationSeconds)}
                        </Text>
                      </BlockStack>
                    </Card>
                    <Card padding="300">
                      <BlockStack gap="100">
                        <Text as="span" tone="subdued">
                          SoC đầu → cuối
                        </Text>
                        <Text as="span" variant="heading2xl">
                          {socSummary}
                        </Text>
                      </BlockStack>
                    </Card>
                  </InlineGrid>

                  <Divider />

                  <InlineGrid columns={{ xs: '1', sm: '1', md: '2', lg: '2' }} gap="400">
                    <Card padding="300">
                      <BlockStack gap="200">
                        <Text as="h3" variant="headingSm">
                          Thông tin xe & trụ sạc
                        </Text>
                        <BlockStack gap="100">
                          <InlineStack align="space-between">
                            <Text as="span" tone="subdued">
                              Xe sạc
                            </Text>
                            <Link
                              onClick={() => {
                                history(`/vehicle_management/view/${transaction.vehicleId}`);
                              }}
                            >
                              <Text as="span">{vehicleLabel}</Text>
                            </Link>
                          </InlineStack>
                          <InlineStack align="space-between">
                            <Text as="span" tone="subdued">
                              Trạm sạc
                            </Text>
                            <Link
                              onClick={() => {
                                history(`/charge_station/view/${transaction.chargeStationId}`);
                              }}
                            >
                              <Text as="span">{stationLabel}</Text>
                            </Link>
                          </InlineStack>
                          <InlineStack align="space-between">
                            <Text as="span" tone="subdued">
                              Trụ/Connector
                            </Text>
                            <Text as="span">{connectorLabel}</Text>
                          </InlineStack>
                        </BlockStack>
                      </BlockStack>
                    </Card>
                    <Card padding="300">
                      <BlockStack gap="200">
                        <Text as="h3" variant="headingSm">
                          Thông tin phiên
                        </Text>
                        <BlockStack gap="100">
                          <InlineStack align="space-between">
                            <Text as="span" tone="subdued">
                              Bắt đầu
                            </Text>
                            <Text as="span">
                              {transaction.startedAt ? dayjs(new Date(transaction.startedAt)).format('HH:mm DD-MM-YYYY') : '-'}
                            </Text>
                          </InlineStack>
                          <InlineStack align="space-between">
                            <Text as="span" tone="subdued">
                              Kết thúc
                            </Text>
                            <Text as="span">
                              {transaction.stoppedAt ? dayjs(new Date(transaction.stoppedAt)).format('HH:mm DD-MM-YYYY') : '-'}
                            </Text>
                          </InlineStack>
                          <InlineStack align="space-between">
                            <Text as="span" tone="subdued">
                              Meter start → stop
                            </Text>
                            <Text as="span">
                              {transaction.meterStart ?? '-'} → {transaction.meterStop ?? '-'}
                            </Text>
                          </InlineStack>
                        </BlockStack>
                      </BlockStack>
                    </Card>
                  </InlineGrid>
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Biểu đồ theo thời gian
                </Text>
                <BlockStack gap="400">
                  <Card>
                    <BlockStack gap="300">
                      <Text as="h3" variant="headingSm">
                        Công suất (kW)
                      </Text>
                      {hasPowerData ? (
                        <ReactECharts option={chartOptions.power} settings={{ notMerge: true }} style={{ minHeight: 300 }} />
                      ) : (
                        <EmptyState heading="Chưa có dữ liệu công suất" image={emptyIMG}>
                          <p>Không có dữ liệu powerKw để hiển thị biểu đồ.</p>
                        </EmptyState>
                      )}
                    </BlockStack>
                  </Card>
                  <Card>
                    <BlockStack gap="300">
                      <Text as="h3" variant="headingSm">
                        Dòng điện (A)
                      </Text>
                      {hasCurrentData ? (
                        <ReactECharts option={chartOptions.current} settings={{ notMerge: true }} style={{ minHeight: 300 }} />
                      ) : (
                        <EmptyState heading="Chưa có dữ liệu dòng điện" image={emptyIMG}>
                          <p>Không có dữ liệu currentA để hiển thị biểu đồ.</p>
                        </EmptyState>
                      )}
                    </BlockStack>
                  </Card>
                  <Card>
                    <BlockStack gap="300">
                      <Text as="h3" variant="headingSm">
                        Điện áp (V)
                      </Text>
                      {hasVoltageData ? (
                        <ReactECharts option={chartOptions.voltage} settings={{ notMerge: true }} style={{ minHeight: 300 }} />
                      ) : (
                        <EmptyState heading="Chưa có dữ liệu điện áp" image={emptyIMG}>
                          <p>Không có dữ liệu voltageV để hiển thị biểu đồ.</p>
                        </EmptyState>
                      )}
                    </BlockStack>
                  </Card>
                </BlockStack>
              </BlockStack>
            </Layout.Section>
          </Layout>
        ) : (
          <Card>
            <EmptyState heading="Không tìm thấy phiên sạc" image={emptyIMG}>
              <p>Vui lòng kiểm tra lại mã phiên sạc hoặc thử lại sau.</p>
            </EmptyState>
          </Card>
        )}
      </Page>
      <br />
      <br />
      <br />
    </>
  );
}

